import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { normalizeClientDetailCategory } from "../utils/clientDetailsCategory";
import {
  CLIENT_CATEGORY_CHANGE_EVENT,
  CLIENT_CATEGORY_STORAGE_KEY,
  readStoredClientCategory,
  writeStoredClientCategory,
} from "../utils/clientCategoryStorage";
import { clientHasBusinessAccess, clientHasRegisteredBusinesses } from "../utils/clientBusinessAccess";

const ClientCategoryContext = createContext(null);

export function getClientTypeFromUserData(userData) {
  const t = String(userData?.client_type ?? "").trim().toLowerCase();
  if (t === "business" || t.startsWith("business")) return "business";
  return "individual";
}

function readUserDataFromStorage() {
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  } catch {
    return {};
  }
}

function readBusinessAccessFromStorage() {
  return clientHasBusinessAccess(readUserDataFromStorage());
}

export function ClientCategoryProvider({ children }) {
  const isClient = localStorage.getItem("user_role") === "client";
  const [hasBusinessAccess, setHasBusinessAccess] = useState(
    readBusinessAccessFromStorage,
  );
  const isBusinessClient = hasBusinessAccess;
  const showCategoryChooser = isClient && hasBusinessAccess;

  const [categoryTab, setCategoryTabState] = useState(readStoredClientCategory);

  useEffect(() => {
    const applyCategory = (value) => {
      setCategoryTabState(normalizeClientDetailCategory(value));
    };

    const onCustom = (event) => {
      applyCategory(event?.detail ?? readStoredClientCategory());
    };

    const onStorage = (event) => {
      if (event.key === CLIENT_CATEGORY_STORAGE_KEY) {
        applyCategory(event.newValue ?? readStoredClientCategory());
      }
      if (event.key === "USER_DATA") {
        setHasBusinessAccess(readBusinessAccessFromStorage());
      }
    };

    window.addEventListener(CLIENT_CATEGORY_CHANGE_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CLIENT_CATEGORY_CHANGE_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!showCategoryChooser) {
      setCategoryTabState("individual");
      return;
    }
    const stored = readStoredClientCategory();
    const userData = readUserDataFromStorage();
    if (
      stored === "business" &&
      !clientHasRegisteredBusinesses(userData)
    ) {
      const next = writeStoredClientCategory("individual");
      setCategoryTabState(next);
      return;
    }
    setCategoryTabState(stored);
  }, [showCategoryChooser, hasBusinessAccess]);

  const setCategoryTab = useCallback((tab) => {
    const normalized = normalizeClientDetailCategory(tab);
    if (normalized === "business") {
      const userData = readUserDataFromStorage();
      if (!clientHasRegisteredBusinesses(userData)) {
        return readStoredClientCategory();
      }
    }
    const next = writeStoredClientCategory(normalized);
    setCategoryTabState(next);
    return next;
  }, []);

  const syncClientType = useCallback(() => {
    setHasBusinessAccess(readBusinessAccessFromStorage());
  }, []);

  /** Refresh business-tab visibility from a full client record (e.g. after getClientById). */
  const syncBusinessAccess = useCallback((client) => {
    if (!client) return;
    const nextAccess = clientHasBusinessAccess(client);
    setHasBusinessAccess((prev) => (prev === nextAccess ? prev : nextAccess));
    if (!isClient) return;
    try {
      const existing = readUserDataFromStorage();
      const merged = { ...existing, ...client };
      const prevJson = JSON.stringify(existing);
      const nextJson = JSON.stringify(merged);
      if (prevJson !== nextJson) {
        localStorage.setItem("USER_DATA", nextJson);
      }
    } catch {
      /* ignore */
    }
  }, [isClient]);

  const value = useMemo(
    () => ({
      categoryTab: showCategoryChooser ? categoryTab : "individual",
      setCategoryTab,
      showCategoryChooser,
      isBusinessClient,
      hasBusinessAccess,
      isClient,
      syncClientType,
      syncBusinessAccess,
    }),
    [
      categoryTab,
      setCategoryTab,
      showCategoryChooser,
      isBusinessClient,
      hasBusinessAccess,
      isClient,
      syncClientType,
      syncBusinessAccess,
    ],
  );

  return (
    <ClientCategoryContext.Provider value={value}>
      {children}
    </ClientCategoryContext.Provider>
  );
}

/** Shared Individual / Business scope for clients with registered businesses. */
export function useClientCategoryTab() {
  const context = useContext(ClientCategoryContext);
  if (context) return context;

  const isClient = localStorage.getItem("user_role") === "client";
  const hasBusinessAccess = readBusinessAccessFromStorage();
  const isBusinessClient = hasBusinessAccess;
  const showCategoryChooser = isClient && hasBusinessAccess;

  return {
    categoryTab: showCategoryChooser ? readStoredClientCategory() : "individual",
    setCategoryTab: writeStoredClientCategory,
    showCategoryChooser,
    isBusinessClient,
    hasBusinessAccess,
    isClient,
    syncClientType: () => {},
    syncBusinessAccess: () => {},
  };
}
